import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type FiltersTrackingLocation, useTrackReturnedEmptyFiltersResult } from '@air/analytics';

import { SEARCH_TOTALS } from '~/constants/react-query-keys';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { type SearchTotals } from '~/types/SearchTotals';
import { filterParamsToTrackingType } from '~/utils/filters/filtersToUrlParamsUtils';
import {
  buildPrivateSearchTotalsQueryKeyPayload,
  getPrivateSearchTotalKey,
  parsePrivateSearchTotalsQueryKeyPayload,
} from '~/utils/search/getPrivateSearchTotalKey';

export const usePrivateSearchTotalsQueryKey = () => {
  const clipsFetchParams = useClipSearchParams();
  const { typesParam } = useFilterParams();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  // `useClipSearchParams` returns a new object every render. The signature keeps this key stable
  // until the payload actually changes, so writers do not `setQueryData` on every render.
  const payloadSignature = JSON.stringify(buildPrivateSearchTotalsQueryKeyPayload(clipsFetchParams, typesParam));
  const privateSearchTotalKey = useMemo(
    () => getPrivateSearchTotalKey(workspaceId, parsePrivateSearchTotalsQueryKeyPayload(payloadSignature)),
    [payloadSignature, workspaceId],
  );

  return { privateSearchTotalKey };
};

export const usePrivateSearchTotals = ({ trackLocation }: { trackLocation: FiltersTrackingLocation }) => {
  const { trackReturnedEmptyFiltersResult } = useTrackReturnedEmptyFiltersResult();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const { privateSearchTotalKey } = usePrivateSearchTotalsQueryKey();
  const reactQueryKey = [SEARCH_TOTALS, privateSearchTotalKey];
  const {
    filters: { customFields, ...filters },
  } = useFiltersContext();

  const { data } = useQuery({
    queryKey: reactQueryKey,
    queryFn: async () => {
      const cachedTotals = queryClient.getQueryData<SearchTotals>(reactQueryKey);

      if (!cachedTotals) {
        return { total: undefined, assetCount: undefined, boardCount: undefined, fileCount: undefined };
      }

      if (!cachedTotals.total) {
        trackReturnedEmptyFiltersResult({
          types: filterParamsToTrackingType({ ...filters, ...customFields }),
          location: trackLocation,
        });
      }

      return {
        assetCount: cachedTotals.assetCount,
        boardCount: cachedTotals.boardCount,
        fileCount: cachedTotals.fileCount,
        total: cachedTotals.total,
      };
    },
    enabled: !!currentWorkspace?.id,
  });

  return {
    assetCount: data?.assetCount,
    boardCount: data?.boardCount,
    fileCount: data?.fileCount,
    total: data?.total,
  };
};
